import { axiosV1 } from 'interceptors/axios';
import { Dispatch } from 'redux';
import { Envelope } from 'types';
import { dispatchError } from 'utils/dispatchError';
import { decryptResponse } from 'utils/token';

type ReferenceType = {
  society?: string;
  boardId?: string;
  councilId?: string;
  'signers.id'?: string[];
};

type PaginationParams = {
  page?: number;
  limit?: number;
};

type EnvelopeFilters = {
  status?: string;
  signatureType?: string;
};

type SignRedirectionProps = {
  envelopeId: string;
  signerId: string;
};

type EnvelopeMetricsResponse = {
  sentEnvelopes: number;
  pendingEnvelopes: number;
  declinedEnvelopes: number;
  completedEnvelopes: number;
  exceededMaxEnvelopes: boolean;
};

export const getSignRedirection =
  ({ envelopeId, signerId }: SignRedirectionProps): any =>
  async (dispatch: Dispatch) => {
    try {
      const query = {
        params: { envelopeId, signerId },
      };

      const { data } = await axiosV1.get(`/signRedirection`, query);

      const decodedData = await decryptResponse(data);
      return decodedData;
    } catch (error) {
      return dispatchError(error, dispatch);
    }
  };

export const getSignersStatus = (envelopeId: string) => async () => {
  try {
    const { data } = await axiosV1.get(`/signersStatus/${envelopeId}`);

    const decodedData = await decryptResponse(data);
    return decodedData;
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    throw error;
  }
};

export const getEnvelopesByReference =
  (
    reference: ReferenceType,
    pagination?: PaginationParams,
    filters?: EnvelopeFilters
  ) =>
  async () => {
    const query = {
      params: {
        ...reference,
        ...(pagination || {}),
        ...(filters?.status && { status: filters.status }),
        ...(filters?.signatureType && { signatureType: filters.signatureType }),
      },
    };
    try {
      const { data } = await axiosV1.get(`/envelopes/`, query);

      const decodedData = await decryptResponse(data);
      return decodedData;
    } catch (error) {
      console.log(error); // eslint-disable-line no-console
      throw error;
    }
  };

export const getEnvelopes = async (
  societyId: string,
  pagination?: PaginationParams,
  filters?: EnvelopeFilters
) => {
  const reference = { society: societyId };
  return getEnvelopesByReference(reference, pagination, filters)();
};

export const getEnvelopesById = async (envelopeIds: string[]) => {
  const body = { ids: envelopeIds };

  try {
    const { data } = await axiosV1.post(`/envelopes/by-ids`, body);

    const decodedData = await decryptResponse(data);

    return decodedData;
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    throw error;
  }
};

export const getEnvelopesPendingBySigners = async ({
  signerIds,
  filters,
}: {
  signerIds: string[];
  filters?: EnvelopeFilters;
}): Promise<Envelope[]> => {
  const body = { signerIds, filters };
  try {
    const { data } = await axiosV1.post(`/envelopes/pending-by-signers`, body);
    const decodedData = await decryptResponse(data);

    return decodedData as Envelope[];
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    throw error;
  }
};

export const getEnvelopesStatusById = async (envelopeIds: string[]) => {
  const body = { ids: envelopeIds };

  try {
    const { data } = await axiosV1.post(`/envelopes/by-ids/status`, body);

    const decodedData = await decryptResponse(data);
    return decodedData;
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    throw error;
  }
};

export const sendEnvelopeReminder = async (
  envelopeId: string,
  envelopeSignatureId: string
) => {
  const body = { envelopeId, envelopeSignatureId };
  try {
    const { data } = await axiosV1.post(`/envelopes/reminder`, body);

    const decodedData = await decryptResponse(data);
    return decodedData;
  } catch (error) {
    return {
      status: 'error',
      message: 'Error sending reminder',
    };
  }
};

export const resendVoidedEnvelope = async (
  envelopeId: string,
  envelopeSignatureId: string
) => {
  const body = { envelopeId, envelopeSignatureId };
  try {
    const { data } = await axiosV1.post(`/envelopes/resend-voided`, body);

    const decodedData = await decryptResponse(data);
    return decodedData;
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    return { status: 'error', message: 'Error resending voided envelope' };
  }
};

export const cancelEnvelope = async (
  envelopeId: string,
  envelopeSignatureId: string
) => {
  const body = { envelopeId, envelopeSignatureId };
  try {
    const { data } = await axiosV1.post(`/envelopes/cancel`, body);

    const decodedData = await decryptResponse(data);
    return decodedData;
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    throw error;
  }
};

export const getEnvelopeMetricsBySociety = async (societyId: string) => {
  try {
    const { data } = await axiosV1.get(`/envelopes/metrics/${societyId}`);

    const decodedData = await decryptResponse(data);
    return decodedData as EnvelopeMetricsResponse;
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    throw error;
  }
};

export const updateEnvelope =
  (envelopeData: Partial<Envelope>) => async (dispatch: any) => {
    try {
      const body = { ...envelopeData };

      const { data } = await axiosV1.put('/envelopes', body);
      const decodedData = await decryptResponse(data);

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const downloadEnvelope = async (envelopeId: string) => {
  try {
    const { data } = await axiosV1.get(`/envelopes/download/${envelopeId}`);

    const decodedData = await decryptResponse(data);
    return decodedData;
  } catch (error) {
    console.log(error); // eslint-disable-line no-console
    throw error;
  }
};
